import React, { useEffect, useState } from 'react';
import { Box, Button as MuiButton, Typography } from '@mui/material';
import axios from '../../utils/axios';
import { API_URL } from '../../constants';
import { BikeIndexCheckStatus } from '../../services/service/types';
import { InventoryBikeDetailsModal } from '../../pages/inventory/relocate/InventoryBikeDetailsModal';

interface BikeIndexStatusButtonProps {
  inspectionId: number;
  imageUrl?: string;
}

interface BikeIndexStatusResponse {
  inspectionId: number;
  sku: string;
  serialNumber: string | null;
  bikeIndexCheckStatus: BikeIndexCheckStatus | undefined;
  bikeIndexCheckedAt: string | null;
}

export const BikeIndexStatusButton: React.FC<BikeIndexStatusButtonProps> = ({
  inspectionId,
  imageUrl,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [status, setStatus] = useState<BikeIndexCheckStatus | undefined>(undefined);

  const loadStatus = async () => {
    if (!inspectionId) return;
    try {
      const res = await axios.get(
        `${API_URL}/service/inspections/${inspectionId}/bikeIndex/status`,
      );
      const data = res?.data as BikeIndexStatusResponse | undefined;
      setStatus(data?.bikeIndexCheckStatus);
    } catch (err) {
      // leave status undefined — button falls back to "Verification Needed"
    }
  };

  const openModal = () => setModalOpen(true);
  const closeModal = () => {
    setModalOpen(false);
    // refresh after the modal closes so a recheck inside the modal updates the button color
    void loadStatus();
  };

  useEffect(() => {
    void loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionId]);

  let buttonLabel: string;
  let buttonColor: string;
  let buttonHoverColor: string;
  if (status === 'suspicious') {
    buttonLabel = '⚠ Suspicious';
    buttonColor = '#c62828';
    buttonHoverColor = '#b71c1c';
  } else if (status === 'clean') {
    buttonLabel = '✓ Verified';
    buttonColor = '#2e7d32';
    buttonHoverColor = '#1b5e20';
  } else if (status === 'verified') {
    buttonLabel = '✓ Manually Verified';
    buttonColor = '#2e7d32';
    buttonHoverColor = '#1b5e20';
  } else {
    buttonLabel = 'Verification Needed';
    buttonColor = '#ed6c02';
    buttonHoverColor = '#b35300';
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 180 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Serial Number Verification</Typography>
        <MuiButton
          size="small"
          variant="contained"
          onClick={openModal}
          sx={{
            backgroundColor: buttonColor,
            color: 'white',
            '&:hover': { backgroundColor: buttonHoverColor },
            textTransform: 'none',
            py: 0.25,
          }}
        >
          {buttonLabel}
        </MuiButton>
      </Box>
      {modalOpen && (
        <InventoryBikeDetailsModal
          open={modalOpen}
          handleClose={closeModal}
          inspectionId={inspectionId}
          initialImageUrl={imageUrl}
        />
      )}
    </>
  );
};

export default BikeIndexStatusButton;
